import { ZERO_WEI } from '@kwenta/sdk/constants'
import { FuturesMarginType, OrderTypeEnum, PerpsProvider, PositionSide } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, formatPercent, toWei } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AcceptWarningView from 'components/AcceptWarningView'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxRow } from 'components/InfoBox'
import PreviewArrow from 'components/PreviewArrow'
import Spacer from 'components/Spacer'
import { setShowEditPositionModal } from 'state/app/reducer'
import {
	clearTradeInputs,
	editPositionSize,
	handleOneClickOperation,
	refetchTradePreview,
	submitIsolatedLimitOrder,
} from 'state/futures/actions'
import { selectEditPositionInputs, selectPerpsProvider } from 'state/futures/common/selectors'
import { submitIsolatedMarginAdjustPositionSize } from 'state/futures/isolatedMargin/actions'
import {
	selectDelegationTradingWarning,
	selectEditPositionModalInfo,
	selectEditPositionPreview,
	selectIsFetchingTradePreviewEdit,
	selectIsIsolatedMarginProvider,
	selectMaxLeverage,
	selectSubmittingFuturesTx,
} from 'state/futures/selectors'
import {
	submitCrossMarginAdjustPositionSize,
	submitCrossMarginLimitOrder,
} from 'state/futures/snxPerpsV3/actions'
import { selectMaxUsdSizeInputCrossMargin } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector, usePollAction } from 'state/hooks'
import { providerIsCrossMargin, unserializePositionDetails } from 'utils/futures'

import UnderlineTabs from 'components/Button/UnderlineTabs'
import EditPositionFeeInfo from '../FeeInfoBox/EditPositionFeeInfo'

import Currency from 'components/Currency'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { usePrefetchTransaction } from 'hooks/usePrefetchTransaction'
import isEmpty from 'lodash/isEmpty'
import { setTradePreview } from 'state/futures/reducer'
import styled from 'styled-components'
import PositionType from '../PositionType'
import EditPositionSizeInput from './EditPositionSizeInput'

export default function EditPositionSizeModal() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const isFetchingPreview = useAppSelector(selectIsFetchingTradePreviewEdit)
	const preview = useAppSelector(selectEditPositionPreview, isEqual)
	const showDelegationTradingWarning = useAppSelector(selectDelegationTradingWarning)
	const isIsolatedMargin = useAppSelector(selectIsIsolatedMarginProvider)
	const maxUsdValueCrossMargin = useAppSelector(selectMaxUsdSizeInputCrossMargin)
	const provider = useAppSelector(selectPerpsProvider)
	const { trackTrade } = useAnalytics()

	usePollAction('refresh_preview', refetchTradePreview, {
		dependencies: [preview?.toString()],
		intervalTime: 12000,
		disabled: !preview,
	})

	const { nativeSizeDelta, orderPrice, orderType } = useAppSelector(
		selectEditPositionInputs,
		isEqual
	)
	const {
		market,
		position: serializedPosition,
		marketPrice,
		indexPrice,
	} = useAppSelector(selectEditPositionModalInfo, isEqual)
	const maxLeverage = useAppSelector(selectMaxLeverage)

	const [overridePriceProtection, setOverridePriceProtection] = useState(false)
	const [editType, setEditType] = useState(0)

	const { getRequest, isFetching } = usePrefetchTransaction({
		stagedTx: preview?.stagedTx,
	})

	const price = useMemo(() => {
		return orderPrice || marketPrice
	}, [orderPrice, marketPrice])

	const positionDetails = useMemo(() => {
		if (!serializedPosition) return null
		return unserializePositionDetails(serializedPosition.details)
	}, [serializedPosition])

	const activePositionDetails = useMemo(() => {
		if (serializedPosition?.details.status === 'open') {
			return unserializePositionDetails(serializedPosition.details)
		}
	}, [serializedPosition])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		dispatch(clearTradeInputs())
	}, [])

	const onChangeTab = useCallback(
		(selection: number) => {
			if (market) {
				dispatch(clearTradeInputs())
			}
			setEditType(selection)
		},
		[dispatch, market]
	)

	const submitSizeChange = useCallback(async () => {
		if (!market) return
		trackTrade(
			EVENT_NAME.SUBMIT_EDIT_POSITION_SIZE,
			market.asset,
			Number(preview?.notionalValue ?? 0)
		)
		if (providerIsCrossMargin(provider)) {
			// Depending on whether it is market or limit price, we need to submit different actions
			if (orderPrice) {
				dispatch(submitCrossMarginLimitOrder(overridePriceProtection))
			} else {
				const request = await getRequest()
				dispatch(submitCrossMarginAdjustPositionSize({ overridePriceProtection, request }))
			}
		} else {
			// Depending on whether it is market or limit price, we need to submit different actions
			if (orderPrice) {
				dispatch(submitIsolatedLimitOrder(overridePriceProtection))
			} else {
				dispatch(submitIsolatedMarginAdjustPositionSize(overridePriceProtection))
			}
		}
	}, [
		overridePriceProtection,
		provider,
		orderPrice,
		market,
		preview?.notionalValue,
		getRequest,
		trackTrade,
		dispatch,
	])

	const onClick = useCallback(
		() => dispatch(handleOneClickOperation(submitSizeChange)),
		[dispatch, submitSizeChange]
	)

	const isLoading = useMemo(
		() => isSubmitting || isFetchingPreview || isFetching,
		[isSubmitting, isFetchingPreview, isFetching]
	)

	const resultingLeverage = useMemo(() => {
		if (!preview || !positionDetails) return
		return positionDetails.margin.remainingMargin?.gt(0)
			? wei(preview.newSize).mul(price).div(positionDetails.margin.remainingMargin).abs()
			: wei(0)
	}, [preview, positionDetails, price])

	const maxNativeIncreaseValueCrossMargin = useMemo(() => {
		if (!price || wei(price).eq(0)) return ZERO_WEI
		return wei(maxUsdValueCrossMargin).div(price)
	}, [maxUsdValueCrossMargin, price])

	const maxNativeIncreaseValueIsolatedMargin = useMemo(() => {
		if (!price || wei(price).eq(0)) return ZERO_WEI
		const maxPosLeverage = positionDetails?.margin.maxLeverage ?? wei(maxLeverage)
		const totalMax = positionDetails?.margin.remainingMargin?.mul(maxPosLeverage) ?? ZERO_WEI
		let max = totalMax.sub(activePositionDetails?.margin.notionalValue ?? 0)
		max = max.gt(0) ? max : ZERO_WEI
		return max.div(price)
	}, [
		price,
		positionDetails?.margin.maxLeverage,
		positionDetails?.margin.remainingMargin,
		maxLeverage,
		activePositionDetails?.margin.notionalValue,
	])

	const maxNativeIncreaseValue = useMemo(
		() =>
			market?.marginType === FuturesMarginType.ISOLATED_MARGIN
				? maxNativeIncreaseValueIsolatedMargin
				: maxNativeIncreaseValueCrossMargin,
		[maxNativeIncreaseValueIsolatedMargin, maxNativeIncreaseValueCrossMargin, market?.marginType]
	)

	const maxNativeValue = useMemo(() => {
		return editType === 0 ? maxNativeIncreaseValue : activePositionDetails?.size ?? ZERO_WEI
	}, [editType, maxNativeIncreaseValue, activePositionDetails?.size])

	const minNativeValue = useMemo(() => {
		if (editType === 0) return ZERO_WEI
		// If a user is over max leverage they can only
		// decrease to a value below max leverage in snx v2
		if (
			market?.provider === PerpsProvider.SNX_V2_OP &&
			positionDetails?.margin.leverage?.gt(toWei(maxLeverage))
		) {
			const safeSize =
				positionDetails.margin.remainingMargin?.mul(toWei(maxLeverage)).div(price) ?? wei(0)
			return positionDetails.size.sub(safeSize)
		}
		return ZERO_WEI
	}, [maxLeverage, positionDetails, editType, price, market?.provider])

	const maxNativeValueWithBuffer = useMemo(() => {
		if (editType === 1) return maxNativeValue
		return maxNativeValue.add(maxNativeValue.mul(0.001))
	}, [maxNativeValue, editType])

	const sizeWei = useMemo(
		() =>
			!nativeSizeDelta || Number.isNaN(Number(nativeSizeDelta)) ? wei(0) : wei(nativeSizeDelta),
		[nativeSizeDelta]
	)

	const maxLeverageExceeded = useMemo(() => {
		return editType === 0 && activePositionDetails?.margin.leverage?.gt(toWei(maxLeverage))
	}, [editType, activePositionDetails?.margin.leverage, maxLeverage])

	const invalid = useMemo(
		() => sizeWei.abs().gt(maxNativeValueWithBuffer),
		[sizeWei, maxNativeValueWithBuffer]
	)

	const side = useMemo(
		() => activePositionDetails?.side ?? PositionSide.LONG,
		[activePositionDetails?.side]
	)

	const minusSizeDelta = useMemo(() => {
		return (
			(side === PositionSide.LONG && editType === 1) ||
			(side === PositionSide.SHORT && editType === 0)
		)
	}, [side, editType])

	const priceInvalid = useMemo(() => {
		return (
			orderType === OrderTypeEnum.LIMIT &&
			!isEmpty(orderPrice) &&
			((minusSizeDelta && wei(orderPrice).lte(indexPrice ?? 0)) ||
				(!minusSizeDelta && wei(orderPrice || 0).gte(indexPrice ?? 0)))
		)
	}, [orderPrice, orderType, indexPrice, minusSizeDelta])

	const previewError = useMemo(() => {
		if (preview?.exceedsPriceProtection && !overridePriceProtection)
			return 'Exceeds Price Protection'
		if (maxLeverageExceeded) return 'Max leverage exceeded'
		if (preview?.statusMessage && preview.statusMessage !== 'Success') return preview?.statusMessage
		return null
	}, [
		preview?.statusMessage,
		preview?.exceedsPriceProtection,
		maxLeverageExceeded,
		overridePriceProtection,
	])

	const submitDisabled = useMemo(() => {
		return (
			sizeWei.eq(0) ||
			invalid ||
			priceInvalid ||
			isLoading ||
			!!previewError ||
			showDelegationTradingWarning ||
			(orderType === OrderTypeEnum.LIMIT && !orderPrice)
		)
	}, [
		sizeWei,
		invalid,
		priceInvalid,
		isLoading,
		previewError,
		showDelegationTradingWarning,
		orderType,
		orderPrice,
	])

	const onClose = () => {
		if (market) {
			dispatch(editPositionSize(''))
			dispatch(
				setTradePreview({
					preview: undefined,
					provider: market.provider,
				})
			)
		}
		dispatch(setShowEditPositionModal(null))
	}

	const editTabs = useMemo(
		() => [
			{
				name: 'Increase',
				label: t('futures.market.trade.edit-position.increase'),
				active: editType === 0,
				onClick: (): void => {
					onChangeTab(0)
				},
			},
			{
				name: 'Decrease',
				label: t('futures.market.trade.edit-position.decrease'),
				active: editType === 1,
				onClick: (): void => {
					onChangeTab(1)
				},
			},
		],
		[editType, onChangeTab, t]
	)

	const pnl = useMemo(() => {
		if (positionDetails?.size === undefined || positionDetails?.size.eq(0) || !preview) {
			return wei(0)
		}
		const marketPnl = wei(positionDetails.pnl.totalPnl?.pnl || 0)
			.mul(wei(positionDetails.size).sub(preview.newSize))
			.div(wei(positionDetails.size))
		// if orderType is limit, additional pnl should be calculated
		if (orderType === OrderTypeEnum.LIMIT && orderPrice) {
			const orderPnl = wei(orderPrice)
				.sub(marketPrice)
				.mul(wei(positionDetails.size).sub(preview.newSize))
			return marketPnl.add(orderPnl)
		}
		return marketPnl
	}, [positionDetails, preview, orderType, orderPrice, marketPrice])

	return (
		<StyledBaseModal
			title={
				<FlexDivCol>
					<Body size="large">Edit Size</Body>
					<FlexDivRowCentered justifyContent="left">
						<Body weight="bold" color="secondary">
							{market?.marketName}
						</Body>
						<Spacer width={5} />
						<PositionType side={side} />
					</FlexDivRowCentered>
				</FlexDivCol>
			}
			isOpen
			onDismiss={onClose}
		>
			<Divider height={1} divider />
			<TabButtonsContainer>
				<UnderlineTabs options={editTabs} />
			</TabButtonsContainer>
			<Spacer height={20} />

			<EditPositionSizeInput
				minNativeValue={minNativeValue.lt(maxNativeValue) ? minNativeValue.toString() : '0'}
				maxNativeValue={maxNativeValue.toString()}
				type={editType === 0 ? 'increase' : 'decrease'}
				priceInvalid={priceInvalid}
				minusSizeDelta={minusSizeDelta}
			/>

			<Spacer height={20} />
			{isIsolatedMargin && (
				<InfoBoxRow
					textValueIcon={
						resultingLeverage && (
							<PreviewArrow showPreview>{resultingLeverage.toString(2)}x</PreviewArrow>
						)
					}
					title={t('futures.market.trade.edit-position.leverage-change')}
					textValue={
						activePositionDetails?.margin.leverage
							? `${activePositionDetails.margin.leverage.toString(2)}x`
							: '-'
					}
				/>
			)}

			<InfoBoxRow
				textValueIcon={
					preview?.newSize && (
						<PreviewArrow showPreview>
							{providerIsCrossMargin(market?.provider) || positionDetails?.margin.remainingMargin
								? formatNumber(wei(preview.newSize).abs(), { suggestDecimals: true })
								: '-'}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-position.position-size')}
				textValue={formatNumber(activePositionDetails?.size || 0, { suggestDecimals: true })}
			/>
			<InfoBoxRow
				textValueIcon={
					preview?.liqPrice && (
						<PreviewArrow showPreview>
							{preview ? formatDollars(preview.liqPrice, { suggestDecimals: true }) : '-'}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-position.liquidation')}
				textValue={formatDollars(activePositionDetails?.price.liquidationPrice || 0, {
					suggestDecimals: true,
				})}
			/>
			<InfoBoxRow
				title={t('futures.market.trade.edit-position.fill-price')}
				textValue={`${formatDollars(preview?.fillPrice || 0, {
					suggestDecimalsForAsset: market?.asset,
				})}(${formatPercent(preview?.priceImpact || 0, {
					suggestDecimals: true,
					maxDecimals: 4,
				})})`}
			/>

			{editType === 1 && (
				<>
					<Spacer height={20} />
					<InfoBoxRow
						title={t('futures.market.trade.edit-position.pnl')}
						nodeValue={<Currency.Price price={pnl} colored />}
					/>
				</>
			)}

			{preview?.exceedsPriceProtection && (
				<>
					<Spacer height={20} />
					<AcceptWarningView
						message={t('futures.market.trade.confirmation.modal.slippage-warning')}
						checked={overridePriceProtection}
						onChangeChecked={(checked) => setOverridePriceProtection(checked)}
					/>
				</>
			)}
			<Spacer height={20} />

			<TransactionButton
				loading={isLoading}
				variant="solid-highlight"
				data-testid="submit-edit-position-size-button"
				disabled={submitDisabled}
				fullWidth
				onClick={onClick}
			>
				{t('futures.market.trade.edit-position.confirm')}
			</TransactionButton>

			{previewError && (
				<>
					<Spacer height={20} />
					<ErrorView message={previewError} formatter="revert" />
				</>
			)}
			{showDelegationTradingWarning && (
				<ErrorView
					messageType="error"
					message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
			<Spacer height={20} />
			<EditPositionFeeInfo />
		</StyledBaseModal>
	)
}

export const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 438px;
	}
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`
const Divider = styled(Spacer)`
	width: 100%;
	margin-bottom: 10px;
`
